/**
 * useMCPFormState Hook
 *
 * Queries DOM for form metadata and exposes to MCP bridge.
 * SECURITY: Never exposes form VALUES - only validation state.
 * This prevents PII exposure while allowing agents to check form validity.
 */

import { useState, useEffect, useCallback } from 'react';

// Throttle form state updates to prevent performance issues
const THROTTLE_MS = 200;

/**
 * Get form metadata from DOM (no values - security)
 */
function getFormMetadata() {
  const forms = {};

  // Find all forms with data-testid
  document.querySelectorAll('[data-testid]').forEach(el => {
    // Check if element is a form or has form data attributes
    const testId = el.getAttribute('data-testid');
    const isForm = el.tagName === 'FORM' || el.hasAttribute('data-form-valid');

    if (!isForm) return;

    const formMeta = {
      valid: el.getAttribute('data-form-valid') === 'true',
      submitting: el.getAttribute('data-form-submitting') === 'true',
      dirty: el.getAttribute('data-form-dirty') === 'true',
      fields: {},
      fieldCount: 0,
      errorCount: 0,
    };

    // Find all fields within the form
    el.querySelectorAll('[data-field-name]').forEach(field => {
      const fieldName = field.getAttribute('data-field-name');
      const fieldValid = field.getAttribute('data-field-valid') !== 'false';
      const fieldError = field.getAttribute('data-field-error') || null;
      const fieldRequired = field.getAttribute('data-field-required') === 'true' || field.required;

      formMeta.fields[fieldName] = {
        valid: fieldValid,
        error: fieldError,
        required: fieldRequired,
        // NEVER include value - security
      };

      formMeta.fieldCount++;
      if (!fieldValid) formMeta.errorCount++;
    });

    // Also check for Mantine form fields (which may not have data attributes)
    el.querySelectorAll('.mantine-TextInput-root, .mantine-Select-root, .mantine-DateInput-root').forEach(wrapper => {
      const input = wrapper.querySelector('input, select');
      if (!input) return;

      const fieldName = input.name || input.id || `field-${formMeta.fieldCount}`;
      if (formMeta.fields[fieldName]) return; // Already processed

      const errorEl = wrapper.querySelector('.mantine-TextInput-error, .mantine-InputWrapper-error');
      const hasError = !!errorEl;

      formMeta.fields[fieldName] = {
        valid: !hasError,
        error: hasError ? errorEl.textContent : null,
        required: input.required || input.hasAttribute('aria-required'),
      };

      formMeta.fieldCount++;
      if (hasError) formMeta.errorCount++;
    });

    forms[testId] = formMeta;
  });

  return forms;
}

export function useMCPFormState() {
  const [forms, setForms] = useState({});

  // Throttled update function
  const updateForms = useCallback(() => {
    const formMetadata = getFormMetadata();
    setForms(formMetadata);
  }, []);

  useEffect(() => {
    // Initial scan
    updateForms();

    // Set up mutation observer to watch for form changes
    let timeoutId = null;
    const observer = new MutationObserver(() => {
      // Throttle updates
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(updateForms, THROTTLE_MS);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-form-valid', 'data-form-dirty', 'data-field-valid', 'data-field-error', 'class'],
    });

    // Also listen for form input events
    const handleInput = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(updateForms, THROTTLE_MS);
    };

    document.addEventListener('input', handleInput, { passive: true });
    document.addEventListener('change', handleInput, { passive: true });

    return () => {
      observer.disconnect();
      document.removeEventListener('input', handleInput);
      document.removeEventListener('change', handleInput);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [updateForms]);

  return forms;
}
